import { useTheme, Box } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePickerProps } from './DatePicker.props';

/**
 * DatePicker Component
 *
 * A customizable date picker calendar component.
 *
 * @example
 * ```tsx
 * const [date, setDate] = useState<Date | null>(null);
 *
 * <DatePicker
 *   value={date}
 *   onChange={setDate}
 * />
 * ```
 */
export const DatePicker = ({
  value,
  onChange,
  size = 's',
  minDate,
  maxDate,
  disablePast = false,
  disableFuture = false,
  slots,
  ...props
}: DatePickerProps) => {
  const theme = useTheme();

  const calendarWidth =
    size === 's' ? '290px' : size === 'm' ? '320px' : '350px';

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          borderRadius: theme.unit(2),
          padding: theme.unit(2),
          width: `calc(${calendarWidth} + ${theme.unit(4)})`,
          boxSizing: 'border-box',
        }}
        {...props}
      >
        <DateCalendar
          value={value}
          onChange={onChange}
          minDate={minDate}
          maxDate={maxDate}
          disablePast={disablePast}
          disableFuture={disableFuture}
          slots={slots}
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            '& .MuiPickersCalendarHeader-root': {
              paddingLeft: theme.unit(2),
              paddingRight: theme.unit(2),
              marginTop: theme.unit(1),
              marginBottom: theme.unit(1),
            },
            '& .MuiPickersCalendarHeader-label': {
              /* M3/label/large */
              ...theme.typography.M3.label.large,
              color: theme.palette.M3.sys.light['on-surface-variant'],
            },
            '& .MuiPickersArrowSwitcher-button': {
              color: theme.palette.M3.sys.light['on-surface-variant'],
            },
            '& .MuiDayCalendar-header': {
              '& .MuiDayCalendar-weekDayLabel': {
                ...theme.typography.M3.label.medium,
                color: theme.palette.M3.sys.light['on-surface-variant'],
                fontWeight: 400,
              },
            },
            '& .MuiYearCalendar-root': {
              width: '100%',
              maxWidth: '100%',
            },
            '& .MuiYearCalendar-button': {
              /* M3/body/large */
              ...theme.typography.M3.body.large,
              color: theme.palette.M3.sys.light['on-surface'],
              '&:hover': {
                backgroundColor:
                  theme.palette.M3.sys.light['primary-container'],
              },
              '&.Mui-selected': {
                backgroundColor: `${theme.palette.M3.sys.light.primary} !important`,
                color: `${theme.palette.M3.sys.light['on-primary']} !important`,
              },
            },
            '& .MuiPickersDay-root': {
              ...theme.typography.M3.body.medium,
              color: theme.palette.M3.sys.light['on-surface'],
              borderRadius: theme.unit(1),
              '&:hover': {
                backgroundColor:
                  theme.palette.M3.sys.light['primary-container'],
              },
              '&.Mui-selected': {
                backgroundColor: `${theme.palette.M3.sys.light.primary} !important`,
                color: `${theme.palette.M3.sys.light['on-primary']} !important`,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: `${theme.palette.M3.sys.light.primary} !important`,
                },
              },
              '&.MuiPickersDay-today': {
                border: `1px solid ${theme.palette.M3.sys.light.primary}`,
                backgroundColor: 'transparent',
                '&:not(.Mui-selected)': {
                  borderColor: theme.palette.M3.sys.light.primary,
                },
              },
            },
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default DatePicker;
